import React, { useEffect, useState } from "react";
import { apiService, Campaign, ChannelType } from "../services/api";
import { Link } from "react-router-dom";
import {
  Plus,
  Trash2,
  MessageSquare,
  AlertTriangle,
  Pencil,
  Wand2,
} from "lucide-react";

const CAMPAIGN_INSTRUCTIONS_PLACEHOLDER = `Campaign Objective:
- Define the final conversion goal and expected outcome.

Tone and Communication Style:
- Define tone, language style, and response constraints.

Discovery Questions:
- List what the sales agent must ask to qualify the lead.

End Goal:
- Define the exact CTA or next action required from the lead.`;

const FOLLOW_UP_INSTRUCTIONS_PLACEHOLDER = `Follow-up Triggers:
- When should follow-up start after no reply? (e.g. 24h, 48h)

Follow-up Sequence:
- Attempt 1 template
- Attempt 2 template
- Attempt 3 template

Max Attempts:
- Define maximum retries before stopping.

Escalation Rules:
- Define when to hand off to human sales or mark as cold lead.`;

const CAMPAIGN_CHANNEL_OPTIONS: Array<{ value: ChannelType; label: string }> = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "facebook_messenger", label: "Messenger" },
  { value: "instagram", label: "Instagram" },
];

const DEFAULT_CAMPAIGN_CHANNELS: ChannelType[] = CAMPAIGN_CHANNEL_OPTIONS.map(
  ({ value }) => value,
);

const normalizeCampaignChannels = (channels?: ChannelType[]): ChannelType[] => {
  const valid = (channels || []).filter((channel) =>
    CAMPAIGN_CHANNEL_OPTIONS.some((option) => option.value === channel),
  );
  if (valid.length === 0) return [...DEFAULT_CAMPAIGN_CHANNELS];
  return Array.from(new Set(valid));
};

type TrelloSummaryField =
  | "lead_goal"
  | "marketing_experience"
  | "measurement_method"
  | "urgency"
  | "objections"
  | "next_action"
  | "lead_label";

type TrelloTemplateStyle = "concise" | "detailed";

interface TrelloFieldMeta {
  key: TrelloSummaryField;
  title: string;
  hint: string;
}

const TRELLO_FIELDS: TrelloFieldMeta[] = [
  {
    key: "lead_goal",
    title: "Lead Goal",
    hint: "What the client wants to achieve.",
  },
  {
    key: "marketing_experience",
    title: "Marketing Experience",
    hint: "Previous ads/agency experience.",
  },
  {
    key: "measurement_method",
    title: "Measurement Method",
    hint: "How they measure marketing success.",
  },
  {
    key: "urgency",
    title: "Urgency",
    hint: "How urgent this lead is right now.",
  },
  {
    key: "objections",
    title: "Objections",
    hint: "Main concerns or blockers.",
  },
  {
    key: "next_action",
    title: "Next Action",
    hint: "Best immediate next step.",
  },
  {
    key: "lead_label",
    title: "Lead Label",
    hint: "Hot / Warm / Cold or your custom wording.",
  },
];

interface TrelloTemplateDraft {
  sectionTitle: string;
  style: TrelloTemplateStyle;
  fallbackText: string;
  includedFields: TrelloSummaryField[];
  requiredFields: TrelloSummaryField[];
  labels: Record<TrelloSummaryField, string>;
}

const defaultTrelloTemplateDraft = (): TrelloTemplateDraft => ({
  sectionTitle: "AI Summary",
  style: "concise",
  fallbackText: "Unknown",
  includedFields: TRELLO_FIELDS.map((field) => field.key),
  requiredFields: ["lead_goal", "next_action", "lead_label"],
  labels: {
    lead_goal: "Lead Goal",
    marketing_experience: "Marketing Experience",
    measurement_method: "Measurement Method",
    urgency: "Urgency",
    objections: "Objections",
    next_action: "Next Action",
    lead_label: "Lead Label",
  },
});

const parseTrelloTemplateDraft = (raw?: string): TrelloTemplateDraft => {
  const fallback = defaultTrelloTemplateDraft();
  const text = (raw || "").trim();
  if (!text) return fallback;

  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== "object") return fallback;

    const included = Array.isArray(parsed.fieldOrder)
      ? parsed.fieldOrder.filter((field: any) =>
          TRELLO_FIELDS.some((item) => item.key === field),
        )
      : [];

    const required = Array.isArray(parsed.requiredFields)
      ? parsed.requiredFields.filter(
          (field: any) =>
            included.includes(field) &&
            TRELLO_FIELDS.some((item) => item.key === field),
        )
      : [];

    const labels = { ...fallback.labels };
    if (parsed.labels && typeof parsed.labels === "object") {
      for (const field of TRELLO_FIELDS) {
        const value = parsed.labels[field.key];
        if (typeof value === "string" && value.trim()) {
          labels[field.key] = value.trim();
        }
      }
    }

    return {
      sectionTitle:
        typeof parsed.sectionTitle === "string" && parsed.sectionTitle.trim()
          ? parsed.sectionTitle.trim()
          : fallback.sectionTitle,
      style: parsed.style === "detailed" ? "detailed" : "concise",
      fallbackText:
        typeof parsed.fallbackText === "string" && parsed.fallbackText.trim()
          ? parsed.fallbackText.trim()
          : fallback.fallbackText,
      includedFields: included.length > 0 ? included : fallback.includedFields,
      requiredFields: required,
      labels,
    };
  } catch {
    return fallback;
  }
};

const buildTrelloTemplatePayload = (draft: TrelloTemplateDraft) => ({
  version: 1,
  sectionTitle: draft.sectionTitle.trim() || "AI Summary",
  style: draft.style,
  fieldOrder: draft.includedFields,
  labels: draft.labels,
  requiredFields: draft.requiredFields,
  fallbackText: draft.fallbackText.trim() || "Unknown",
});

const buildTrelloTemplateJson = (draft: TrelloTemplateDraft): string =>
  JSON.stringify(buildTrelloTemplatePayload(draft), null, 2);

const parseAdvancedTemplateJson = (
  raw: string,
): { parsed?: Record<string, any>; error?: string } => {
  const text = (raw || "").trim();
  if (!text) {
    return { error: "Template JSON is required." };
  }

  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { error: "Template must be a JSON object." };
    }
    return { parsed };
  } catch {
    return { error: "Invalid JSON format." };
  }
};

const useCampaigns = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  const refresh = async () => {
    try {
      const data = await apiService.getCampaigns();
      setCampaigns(data);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return { campaigns, refresh };
};

const useTagInput = (initialTags: string[] = []) => {
  const [tags, setTags] = useState<string[]>(initialTags);
  const [input, setInput] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = input.trim();
      if (trimmed && !tags.includes(trimmed)) {
        setTags([...tags, trimmed]);
        setInput("");
      }
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  return { tags, setTags, input, setInput, handleKeyDown, removeTag };
};

export const CampaignsPage: React.FC = () => {
  const { campaigns, refresh } = useCampaigns();
  const [aiCampaignBrief, setAiCampaignBrief] = useState("");
  const [isGeneratingCampaign, setIsGeneratingCampaign] = useState(false);
  const [campaignGeneratorError, setCampaignGeneratorError] = useState<
    string | null
  >(null);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [campaignChannels, setCampaignChannels] = useState<ChannelType[]>([
    ...DEFAULT_CAMPAIGN_CHANNELS,
  ]);
  const [campaignInstructions, setCampaignInstructions] = useState("");
  const [followUpInstructions, setFollowUpInstructions] = useState("");
  const [trelloTemplateJson, setTrelloTemplateJson] = useState<string>(
    buildTrelloTemplateJson(defaultTrelloTemplateDraft()),
  );
  const [trelloTemplateError, setTrelloTemplateError] = useState<string | null>(
    null,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(
    null,
  );

  const {
    tags: triggers,
    setTags: setTriggers,
    input: triggerInput,
    setInput: setTriggerInput,
    handleKeyDown,
    removeTag,
  } = useTagInput();

  const handleTemplateJsonChange = (value: string) => {
    setTrelloTemplateJson(value);
    const result = parseAdvancedTemplateJson(value);
    setTrelloTemplateError(result.error || null);
  };

  const formatTemplateJson = () => {
    const result = parseAdvancedTemplateJson(trelloTemplateJson);
    if (result.error || !result.parsed) {
      setTrelloTemplateError(result.error || "Invalid JSON format.");
      return;
    }

    const formatted = JSON.stringify(result.parsed, null, 2);
    setTrelloTemplateJson(formatted);
    setTrelloTemplateError(null);
  };

  const resetTemplateToDefault = () => {
    const defaultDraft = defaultTrelloTemplateDraft();
    const defaultJson = buildTrelloTemplateJson(defaultDraft);
    setTrelloTemplateJson(defaultJson);
    setTrelloTemplateError(null);
  };

  const toggleCampaignChannel = (channel: ChannelType) => {
    setCampaignChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((item) => item !== channel)
        : [...prev, channel],
    );
  };

  const resetForm = () => {
    // setAiCampaignBrief("");
    // setIsGeneratingCampaign(false);
    // setCampaignGeneratorError(null);
    setNewCampaignName("");
    setCampaignChannels([...DEFAULT_CAMPAIGN_CHANNELS]);
    setCampaignInstructions("");
    setFollowUpInstructions("");
    resetTemplateToDefault();
    setTriggers([]);
    setTriggerInput("");
    setEditingCampaignId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreating(true);
  };

  const openEditModal = (campaign: Campaign) => {
    const rawTemplate = (campaign.trelloInstructions || "").trim();
    const parsedDraft = parseTrelloTemplateDraft(rawTemplate);
    const templateJson = rawTemplate || buildTrelloTemplateJson(parsedDraft);
    const templateParseResult = parseAdvancedTemplateJson(templateJson);

    setAiCampaignBrief("");
    setCampaignGeneratorError(null);
    setIsGeneratingCampaign(false);
    setNewCampaignName(campaign.name || "");
    setCampaignChannels(normalizeCampaignChannels(campaign.channels));
    setCampaignInstructions(campaign.campaignInstructions || "");
    setFollowUpInstructions(campaign.followUpInstructions || "");
    setTrelloTemplateJson(templateJson);
    setTrelloTemplateError(templateParseResult.error || null);
    setTriggers(campaign.triggers || []);
    setTriggerInput("");
    setEditingCampaignId(campaign._id);
    setIsCreating(true);
  };

  const closeFormModal = () => {
    setIsCreating(false);
    setEditingCampaignId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const finalTriggers = Array.from(
      new Set(
        [...triggers, triggerInput]
          .map((trigger) => trigger.trim())
          .filter(Boolean),
      ),
    );

    const normalizedCampaignName = newCampaignName.trim();
    const normalizedCampaignInstructions = campaignInstructions.trim();
    const normalizedFollowUpInstructions = followUpInstructions.trim();
    let trelloInstructionsPayload = "";
    const templateParseResult = parseAdvancedTemplateJson(trelloTemplateJson);

    const validationErrors: string[] = [];
    if (!normalizedCampaignName)
      validationErrors.push("Campaign name is required.");
    if (campaignChannels.length === 0)
      validationErrors.push("Please select at least one campaign channel.");
    if (finalTriggers.length === 0)
      validationErrors.push("Please add at least one trigger keyword.");
    if (!normalizedCampaignInstructions)
      validationErrors.push("Campaign instructions are required.");
    if (!normalizedFollowUpInstructions)
      validationErrors.push("Campaign follow-up instructions are required.");
    if (templateParseResult.error || !templateParseResult.parsed) {
      validationErrors.push(
        `Trello template JSON is invalid: ${templateParseResult.error || "Invalid JSON format."}`,
      );
    } else {
      trelloInstructionsPayload = JSON.stringify(
        templateParseResult.parsed,
        null,
        2,
      );
    }

    if (validationErrors.length > 0) {
      alert(validationErrors.join("\n"));
      return;
    }

    try {
      const payload = {
        name: normalizedCampaignName,
        channels: campaignChannels,
        triggers: finalTriggers,
        campaignInstructions: normalizedCampaignInstructions,
        followUpInstructions: normalizedFollowUpInstructions,
        trelloInstructions: trelloInstructionsPayload,
      };

      if (editingCampaignId) {
        await apiService.updateCampaign(editingCampaignId, payload);
      } else {
        await apiService.createCampaign(payload);
      }

      closeFormModal();
      resetForm();
      refresh();
    } catch (error) {
      console.error("Error saving campaign:", error);
    }
  };

  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    campaignId: string | null;
  }>({ show: false, campaignId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (id: string) => {
    setDeleteModal({ show: true, campaignId: id });
  };

  const confirmDelete = async () => {
    if (!deleteModal.campaignId) return;

    setIsDeleting(true);
    try {
      await apiService.deleteCampaign(deleteModal.campaignId);
      refresh();
      setDeleteModal({ show: false, campaignId: null });
    } catch (error) {
      console.error("Error deleting campaign:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, campaignId: null });
  };

  return (
    <div className="page-grid">
      <section className="panel span-2">
        <header className="panel-header">
          <div>
            <p className="eyebrow">Marketing</p>
            <h2>Campaigns</h2>
          </div>
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <Link to="/campaign-blueprint" className="btn btn-secondary">
              Blueprint Guide
            </Link>
            <button className="btn btn-primary" onClick={openCreateModal}>
              <Plus size={16} />
              New Campaign
            </button>
          </div>
        </header>

        {isCreating && (
          <div className="modal-overlay" onClick={closeFormModal}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "920px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}>
              <div
                style={{
                  padding: "1.5rem",
                  borderBottom: "1px solid var(--border)",
                }}>
                <h3 style={{ margin: 0 }}>
                  {editingCampaignId ? "Edit Campaign" : "Create New Campaign"}
                </h3>
              </div>

              <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
                <div className="form-group">
                  <label>Campaign Name</label>
                  <input
                    type="text"
                    value={newCampaignName}
                    onChange={(e) => setNewCampaignName(e.target.value)}
                    placeholder="e.g. Summer Sale"
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group"
                style={{
                  marginTop:"0.5rem",
                }}>
                  <label>Trigger Keywords</label>
                  <div
                    className="tag-input-container"
                    onClick={() =>
                      document.getElementById("triggerInput")?.focus()
                    }>
                    {triggers.map((tag) => (
                      <div key={tag} className="tag-pill">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>
                          ×
                        </button>
                      </div>
                    ))}
                    <input
                      id="triggerInput"
                      className="tag-input"
                      value={triggerInput}
                      onChange={(e) => setTriggerInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        triggers.length === 0
                          ? "Type keywords and press Enter"
                          : ""
                      }
                      type="text"
                    />
                  </div>
                  <small
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "0.8rem",
                      marginBottom: "1rem",
                      marginTop: "-0.5rem",
                      display: "block",
                    }}>
                    Triggers are words/phrases that activate this campaign.
                  </small>
                </div>

                <div
                  className="form-group"
                  style={{
                    paddingTop: "0.5rem",
                    paddingBottom: "0.5rem",
                  }}>
                  <label>Campaign Channels</label>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.6rem",
                      marginTop: "0.55rem",
                    }}>
                    {CAMPAIGN_CHANNEL_OPTIONS.map((option) => {
                      const checked = campaignChannels.includes(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleCampaignChannel(option.value)}
                          style={{
                            border: checked
                              ? "1px solid var(--accent)"
                              : "1px solid var(--border)",
                            background: checked
                              ? "rgba(37, 99, 235, 0.12)"
                              : "var(--surface)",
                            color: checked
                              ? "var(--accent)"
                              : "var(--text-primary)",
                            borderRadius: "999px",
                            padding: "0.4rem 0.8rem",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            fontWeight: 500,
                          }}>
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <small
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "0.8rem",
                      display: "block",
                      marginTop: "0.45rem",
                    }}>
                    AI campaign matching and KB retrieval will be limited to these
                    channels.
                  </small>
                </div>

                <div className="form-group"
                style={{
                    paddingTop: "1rem",
                    paddingBottom: "0.5rem",
                  }}>
                  <label>Campaign Instructions (Required)</label>

                  <textarea
                    value={campaignInstructions}
                    onChange={(e) => setCampaignInstructions(e.target.value)}
                    placeholder={CAMPAIGN_INSTRUCTIONS_PLACEHOLDER}
                    rows={8}
                    required
                    className="tag-input-container"
                  />
                </div>

                <div className="form-group"
                
                style={{
                    paddingTop: "1rem",
                    paddingBottom: "0.5rem",
                  }}>
                  <label>Campaign Follow-up Instructions (Required)</label>
                  <textarea
                    value={followUpInstructions}
                    onChange={(e) => setFollowUpInstructions(e.target.value)}
                    placeholder={FOLLOW_UP_INSTRUCTIONS_PLACEHOLDER}
                    rows={8}
                    required
                    className="tag-input-container"
                  />
                </div>

                <div
                  className="form-group "
                  style={{
                    paddingTop: "2rem",
                    paddingBottom: "0.5rem",
                  }}>
                  <label>Campaign Trello Template (JSON)</label>
                  <div
                    style={{
                      border: "1px solid var(--border)",
                      borderRadius: "10px",
                      padding: "1rem",
                      display: "grid",
                      gap: "1rem",
                    }}>
                    <small style={{ color: "var(--text-secondary)" }}>
                      JSON only. Full control with simple helpers.
                    </small>

                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={resetTemplateToDefault}>
                        Load Default Template
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={formatTemplateJson}>
                        Format JSON
                      </button>
                    </div>

                    <div>
                      <label style={{ fontSize: "0.85rem" }}>
                        Template JSON
                      </label>
                      <textarea
                            className="tag-input-container"
             
                        value={trelloTemplateJson}
                        onChange={(e) =>
                          handleTemplateJsonChange(e.target.value)
                        }
                        rows={16}
                        spellCheck={false}
                        style={{ fontFamily: "monospace" }}
                      />
                      {trelloTemplateError && (
                        <small
                          style={{
                            color: "var(--danger)",
                            display: "block",
                            marginTop: "0.45rem",
                          }}>
                          {trelloTemplateError}
                        </small>
                      )}
                    </div>

                    <small style={{ color: "var(--text-secondary)" }}>
                      Recommended keys: `version`, `sectionTitle`, `style`,
                      `fieldOrder`, `labels`, `requiredFields`, `fallbackText`.
                    </small>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "1rem",
                    marginTop: "1.5rem",
                  }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeFormModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingCampaignId ? "Save Changes" : "Create Campaign"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="table">
          <div
            className="table-head"
            style={{ gridTemplateColumns: "1fr 1.2fr 1.8fr 2.2fr 1fr 130px" }}>
            <span>Name</span>
            <span>Channels</span>
            <span>Triggers</span>
            <span>Playbooks</span>
            <span>Created At</span>
            <span>Actions</span>
          </div>
          <div className="table-body">
            {campaigns.length === 0 ? (
              <div className="empty-state">
                <MessageSquare size={48} />
                <p>No campaigns found.</p>
              </div>
            ) : (
              campaigns.map((campaign) => (
                <div
                  key={campaign._id}
                  className="table-row"
                  style={{ gridTemplateColumns: "1fr 1.2fr 1.8fr 2.2fr 1fr 130px" }}>
                  <strong>{campaign.name}</strong>
                  <div className="tags">
                    {normalizeCampaignChannels(campaign.channels).map((channel) => (
                      <span key={channel} className="pill muted">
                        {channel === "facebook_messenger"
                          ? "Messenger"
                          : channel === "whatsapp"
                            ? "WhatsApp"
                            : "Instagram"}
                      </span>
                    ))}
                  </div>
                  <div className="tags">
                    {campaign.triggers.map((trigger, i) => (
                      <span key={i} className="pill muted">
                        {trigger}
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "grid", gap: "6px" }}>
                    <div
                      style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <span
                        className={`pill ${campaign.campaignInstructions ? "success" : "muted"}`}>
                        Instructions
                      </span>
                      <span
                        className={`pill ${campaign.followUpInstructions ? "success" : "muted"}`}>
                        Follow-up
                      </span>
                      <span
                        className={`pill ${campaign.trelloInstructions ? "success" : "muted"}`}>
                        Trello Template
                      </span>
                    </div>
                  </div>
                  <span>
                    {new Date(
                      campaign.createdAt || Date.now(),
                    ).toLocaleDateString()}
                  </span>
                  <div
                    className="action-buttons"
                    style={{ display: "flex", gap: "8px" }}>
                    <button
                      className="btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(campaign);
                      }}
                      title="Edit campaign"
                      style={{
                        transition: "all 0.2s ease",
                        background: "transparent",
                        color: "var(--accent)",
                        border: "none",
                        outline: "none",
                        padding: "4px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background =
                          "rgba(37, 99, 235, 0.12)";
                        e.currentTarget.style.transform = "scale(1.1)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.transform = "scale(1)";
                      }}>
                      <Pencil size={16} />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(campaign._id);
                      }}
                      title="Delete campaign"
                      style={{
                        transition: "all 0.2s ease",
                        background: "transparent",
                        color: "var(--danger)",
                        border: "none",
                        outline: "none",
                        padding: "4px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = "var(--danger-soft)";
                        e.currentTarget.style.transform = "scale(1.1)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.transform = "scale(1)";
                      }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {deleteModal.show && (
          <div className="modal-overlay" onClick={cancelDelete}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "450px", padding: "24px" }}>
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <div
                  style={{
                    display: "inline-flex",
                    background: "var(--danger-soft)",
                    borderRadius: "50%",
                    padding: "12px",
                    marginBottom: "16px",
                  }}>
                  <AlertTriangle size={32} color="var(--danger)" />
                </div>
                <h3 style={{ margin: "0 0 8px" }}>Delete Campaign</h3>
                <p
                  style={{
                    color: "var(--text-secondary)",
                    margin: "0 0 24px",
                  }}>
                  Are you sure you want to delete this campaign? This action
                  cannot be undone.
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  paddingTop: "16px",
                  borderTop: "1px solid var(--border)",
                }}>
                <button
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={isDeleting}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  style={{
                    background: "var(--danger)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    opacity: isDeleting ? 0.7 : 1,
                    cursor: isDeleting ? "not-allowed" : "pointer",
                  }}>
                  {isDeleting ? (
                    <>
                      <span
                        className="spinner"
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          display: "inline-block",
                        }}></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
      {/* @ts-ignore - styled-jsx types */}
      <style jsx global>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .btn-danger:hover {
          background: var(--danger-soft) !important;
        }

        .btn-icon {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};
